/**
 * DirectorioForm.js - Formulario para agregar/editar negocios
 * Conectado a Firestore via placesService
 * Con tabs para Datos del negocio y Seguimiento (CRM)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { createPlace, updatePlace, deletePlace, verifyPlace } from '../../localizador/services/placesService';
import { getAuthHeaders } from '../../services/api/utils/fetchWrapper';
import SeguimientoTab from './SeguimientoTab';
import VincularGoogleModal from './VincularGoogleModal';

// Categorias por tipo (usando IDs que coinciden con el modelo)
const categoriasEstablecimiento = [
  { id: 'Veterinaria', nombre: 'Veterinaria' },
  { id: 'Farmacia', nombre: 'Farmacia' },
  { id: 'Parques', nombre: 'Parques' },
  { id: 'Pension', nombre: 'Pension' },
  { id: 'Estilistas', nombre: 'Estilistas' },
  { id: 'Cerrajeria', nombre: 'Cerrajeria' },
  { id: 'Farmacia 24h', nombre: 'Farmacia 24h' },
  { id: 'Conveniencia', nombre: 'Conveniencia' }
];

const categoriasProveedor = [
  { id: 'Paseadores', nombre: 'Paseadores' },
  { id: 'Entrenadores', nombre: 'Entrenadores' },
  { id: 'Cuidadores', nombre: 'Cuidadores' },
  { id: 'Estilistas', nombre: 'Estilistas' }
];

// Servicios por categoria
const serviciosPorCategoria = {
  'Veterinaria': ['Consulta', 'Vacunacion', 'Cirugia', 'Urgencias', 'Rayos X', 'Hospitalizacion', 'Estetica', 'Laboratorio'],
  'Estilistas': ['Bano', 'Corte', 'Unas', 'Limpieza dental', 'Spa'],
  'Paseadores': ['Paseo individual', 'Paseo grupal', 'Entrenamiento basico'],
  'Entrenadores': ['Obediencia basica', 'Obediencia avanzada', 'Correccion de conducta', 'Socializacion'],
  'Cuidadores': ['Cuidado en casa', 'Hospedaje', 'Visitas diarias']
};

const metodosPagoOpciones = ['efectivo', 'transferencia', 'tarjeta'];

export default function DirectorioForm({
  negocio,
  tipoNegocio,
  onVolver,
  onGuardado,
  addToHistory
}) {
  const isEditing = !!negocio;
  const tipo = negocio?.tipo || tipoNegocio;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('datos');
  const [currentNegocio, setCurrentNegocio] = useState(negocio);
  const [showGoogleModal, setShowGoogleModal] = useState(false);

  // Estado para liga de edicion
  const [editLink, setEditLink] = useState(null);
  const [loadingEditLink, setLoadingEditLink] = useState(false);
  const [editLinkCopied, setEditLinkCopied] = useState(false);

  // Estado del formulario
  const [formData, setFormData] = useState({
    categoria: '',
    nombre: '',
    telefono: '',
    tieneWhatsapp: false,
    whatsappMismoNumero: true, // true = mismo que teléfono, false = número diferente
    whatsapp: '', // solo se usa si tieneWhatsapp=true y whatsappMismoNumero=false
    horario: '',
    is24h: false,
    direccion: '',
    ciudad: '',
    estado: '',
    zonas: '',
    metodosPago: [],
    estacionamiento: {
      tipo: null,       // 'propio' | 'calle' | 'plaza' | 'sin' | null
      facilidad: null,  // 'muy_facil' | 'facil' | 'medio' | 'dificil' | null
      lugares: null     // number | null (solo si tipo === 'propio')
    },
    servicios: [],
    fotos: [],
    notasInternas: '',
    status: 'pendiente'
  });

  // Cargar datos si es edicion
  useEffect(() => {
    if (negocio) {
      setCurrentNegocio(negocio);

      // Determinar estado de WhatsApp basado en datos existentes
      const tieneWhatsapp = negocio.tieneWhatsapp || !!negocio.whatsapp || false;
      const whatsappMismoNumero = negocio.whatsapp ? false : true; // Si tiene whatsapp distinto, no es mismo número

      // Manejar estacionamiento (puede ser string antiguo u objeto nuevo)
      let estacionamientoData = { tipo: null, facilidad: null, lugares: null };
      if (negocio.estacionamiento && typeof negocio.estacionamiento === 'object') {
        estacionamientoData = {
          tipo: negocio.estacionamiento.tipo || null,
          facilidad: negocio.estacionamiento.facilidad || null,
          lugares: negocio.estacionamiento.lugares || null
        };
      }
      // Si es string antiguo, ignorar (se muestra como "sin info")

      setFormData({
        categoria: negocio.categoria || '',
        nombre: negocio.nombre || '',
        telefono: negocio.telefono || '',
        tieneWhatsapp: tieneWhatsapp,
        whatsappMismoNumero: whatsappMismoNumero,
        whatsapp: negocio.whatsapp || '',
        horario: negocio.horario || '',
        is24h: negocio.is24h || false,
        direccion: negocio.direccion || '',
        ciudad: negocio.ciudad || '',
        estado: negocio.estado || '',
        zonas: Array.isArray(negocio.zonas) ? negocio.zonas.join(', ') : (negocio.zonas || ''),
        metodosPago: negocio.metodosPago || [],
        estacionamiento: estacionamientoData,
        servicios: negocio.servicios || [],
        fotos: negocio.fotos || [],
        notasInternas: negocio.notasInternas || '',
        status: negocio.status || 'pendiente'
      });
    }
  }, [negocio]);

  // Cargar liga de edicion existente
  useEffect(() => {
    async function cargarEditLink() {
      if (!negocio?.id) return;

      try {
        const API_URL = process.env.REACT_APP_API_URL || '';
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_URL}/api/directory/places/${negocio.id}/edit-link`, {
          headers
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data) {
            setEditLink(data.data);
          }
        }
      } catch (err) {
        console.warn('No se pudo cargar liga de edicion:', err);
      }
    }

    cargarEditLink();
  }, [negocio?.id]);

  const categorias = tipo === 'establecimiento' ? categoriasEstablecimiento : categoriasProveedor;

  const serviciosDisponibles = useMemo(() => {
    return serviciosPorCategoria[formData.categoria] || [];
  }, [formData.categoria]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleCheckboxArrayChange = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  // Generar liga de edicion
  const handleGenerarEditLink = async () => {
    if (!negocio?.id) return;

    setLoadingEditLink(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/directory/places/${negocio.id}/generate-edit-link`, {
        method: 'POST',
        headers
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error generando liga');
      }

      setEditLink(data.data);
      if (addToHistory) {
        addToHistory(`Liga de edicion generada para: ${formData.nombre}`);
      }
    } catch (err) {
      console.error('Error generando liga:', err);
      alert('Error al generar liga: ' + err.message);
    } finally {
      setLoadingEditLink(false);
    }
  };

  // Copiar liga al portapapeles
  const handleCopiarEditLink = async () => {
    if (!editLink?.link) return;

    try {
      await navigator.clipboard.writeText(editLink.link);
      setEditLinkCopied(true);
      setTimeout(() => setEditLinkCopied(false), 2000);
    } catch (err) {
      console.error('Error copiando:', err);
      // Fallback para navegadores que no soportan clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = editLink.link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setEditLinkCopied(true);
      setTimeout(() => setEditLinkCopied(false), 2000);
    }
  };

  // Invalidar liga de edicion
  const handleInvalidarEditLink = async () => {
    if (!negocio?.id || !editLink) return;

    if (!window.confirm('Invalidar la liga de edicion actual? El negocio no podra usarla mas.')) return;

    setLoadingEditLink(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/api/directory/places/${negocio.id}/edit-link`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Error invalidando liga');
      }

      setEditLink(null);
      if (addToHistory) {
        addToHistory(`Liga de edicion invalidada para: ${formData.nombre}`);
      }
    } catch (err) {
      console.error('Error invalidando liga:', err);
      alert('Error al invalidar liga: ' + err.message);
    } finally {
      setLoadingEditLink(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Preparar datos
      // WhatsApp: si tiene WhatsApp y es mismo número, guardar null en whatsapp
      // Si tiene WhatsApp y es número diferente, guardar el número diferente
      const whatsappValue = formData.tieneWhatsapp
        ? (formData.whatsappMismoNumero ? null : formData.whatsapp.trim() || null)
        : null;

      // Preparar estacionamiento (solo si tiene tipo seleccionado)
      const estacionamientoData = tipo === 'establecimiento' && formData.estacionamiento.tipo
        ? {
            tipo: formData.estacionamiento.tipo,
            facilidad: formData.estacionamiento.facilidad || null,
            lugares: formData.estacionamiento.tipo === 'propio'
              ? (formData.estacionamiento.lugares || null)
              : null
          }
        : null;

      const data = {
        tipo,
        categoria: formData.categoria,
        nombre: formData.nombre.trim(),
        telefono: formData.telefono.trim() || null,
        tieneWhatsapp: formData.tieneWhatsapp,
        whatsapp: whatsappValue,
        horario: formData.horario.trim() || null,
        is24h: formData.is24h,
        direccion: tipo === 'establecimiento' ? (formData.direccion.trim() || null) : null,
        ciudad: tipo === 'establecimiento' ? (formData.ciudad.trim() || null) : null,
        estado: tipo === 'establecimiento' ? (formData.estado.trim() || null) : null,
        zonas: tipo === 'proveedor' && formData.zonas.trim()
          ? formData.zonas.split(',').map(z => z.trim()).filter(z => z)
          : [],
        metodosPago: formData.metodosPago,
        estacionamiento: estacionamientoData,
        servicios: formData.servicios,
        fotos: formData.fotos,
        notasInternas: formData.notasInternas.trim() || '',
        status: formData.status
      };

      let result;
      if (isEditing) {
        result = await updatePlace(negocio.id, data);
        if (addToHistory) {
          addToHistory(`Negocio actualizado: ${formData.nombre}`);
        }
        setCurrentNegocio(result);
      } else {
        result = await createPlace(data);
        if (addToHistory) {
          addToHistory(`Nuevo negocio creado: ${formData.nombre}`);
        }
      }

      onGuardado(result);

    } catch (err) {
      console.error('Error guardando negocio:', err);
      setError(err.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async () => {
    if (!window.confirm(`Seguro que deseas eliminar "${formData.nombre}"?`)) return;

    setLoading(true);
    setError(null);

    try {
      await deletePlace(negocio.id);
      if (addToHistory) {
        addToHistory(`Negocio eliminado: ${formData.nombre}`);
      }
      onGuardado(null, true); // segundo param indica que fue eliminado
    } catch (err) {
      console.error('Error eliminando negocio:', err);
      setError(err.message || 'Error al eliminar');
      setLoading(false);
    }
  };

  const handleVerificar = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await verifyPlace(negocio.id);
      if (addToHistory) {
        addToHistory(`Negocio verificado: ${formData.nombre}`);
      }
      setFormData(prev => ({ ...prev, status: 'verificado' }));
      setCurrentNegocio(result);
      alert('Lugar verificado correctamente');
    } catch (err) {
      console.error('Error verificando negocio:', err);
      setError(err.message || 'Error al verificar');
    } finally {
      setLoading(false);
    }
  };

  // Handler para cuando se actualiza el negocio desde SeguimientoTab
  const handleNegocioUpdated = (updatedNegocio) => {
    setCurrentNegocio(updatedNegocio);
  };

  return (
    <div className="card">
      <div className="card-header bg-white">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <button
              className="btn btn-link p-0 me-3 text-dark"
              onClick={onVolver}
              disabled={loading}
            >
              <i className="bi bi-arrow-left fs-5"></i>
            </button>
            <h5 className="mb-0">
              {isEditing ? 'Editar negocio' : `Datos del ${tipo === 'establecimiento' ? 'establecimiento' : 'proveedor'}`}
            </h5>
          </div>
          {isEditing && (
            <div className="d-flex gap-2">
              {currentNegocio?.status === 'pendiente' && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleVerificar}
                  disabled={loading}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  Verificar
                </button>
              )}
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={handleEliminar}
                disabled={loading}
              >
                <i className="bi bi-trash me-1"></i>
                Eliminar
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs (solo en modo edicion) */}
      {isEditing && (
        <div className="card-header bg-white border-top-0 pt-0">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'datos' ? 'active' : ''}`}
                onClick={() => setActiveTab('datos')}
              >
                <i className="bi bi-building me-1"></i>
                Datos del negocio
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'seguimiento' ? 'active' : ''}`}
                onClick={() => setActiveTab('seguimiento')}
              >
                <i className="bi bi-person-lines-fill me-1"></i>
                Seguimiento
                {currentNegocio?.contacto?.proximoContacto && (
                  <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.65rem' }}>!</span>
                )}
              </button>
            </li>
          </ul>
        </div>
      )}

      <div className="card-body">
        {/* Tab: Datos del negocio */}
        {(activeTab === 'datos' || !isEditing) && (
          <>
            {/* Error */}
            {error && (
              <div className="alert alert-danger py-2 mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Info de sugerencia */}
            {negocio?.source === 'user_suggestion' && (
              <div className="alert alert-info py-2 mb-3">
                <i className="bi bi-person-badge me-2"></i>
                Este lugar fue sugerido por un usuario
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Categoria */}
              <div className="mb-3">
                <label className="form-label">
                  Categoria <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={formData.categoria}
                  onChange={(e) => {
                    handleChange('categoria', e.target.value);
                    handleChange('servicios', []); // Reset servicios al cambiar categoria
                  }}
                  required
                  disabled={loading}
                >
                  <option value="">Seleccionar...</option>
                  {categorias.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Nombre */}
              <div className="mb-3">
                <label className="form-label">
                  {tipo === 'establecimiento' ? 'Nombre del negocio' : 'Nombre'} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              {/* Telefono */}
              <div className="mb-3">
                <label className="form-label">
                  {tipo === 'establecimiento' ? 'Telefono' : 'Telefono / WhatsApp'}
                </label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  disabled={loading}
                />
              </div>

              {/* WhatsApp (solo establecimiento) */}
              {tipo === 'establecimiento' && (
                <div className="mb-3">
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="tieneWhatsapp"
                      checked={formData.tieneWhatsapp}
                      onChange={(e) => handleChange('tieneWhatsapp', e.target.checked)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="tieneWhatsapp">
                      Tiene WhatsApp
                    </label>
                  </div>

                  {formData.tieneWhatsapp && (
                    <div className="ms-4 ps-2 border-start">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="whatsapp-mismo"
                          name="whatsappOpcion"
                          checked={formData.whatsappMismoNumero}
                          onChange={() => handleChange('whatsappMismoNumero', true)}
                          disabled={loading}
                        />
                        <label className="form-check-label" htmlFor="whatsapp-mismo">
                          Mismo numero del telefono
                        </label>
                      </div>
                      <div className="form-check mt-1">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="whatsapp-diferente"
                          name="whatsappOpcion"
                          checked={!formData.whatsappMismoNumero}
                          onChange={() => handleChange('whatsappMismoNumero', false)}
                          disabled={loading}
                        />
                        <label className="form-check-label" htmlFor="whatsapp-diferente">
                          Numero diferente
                        </label>
                      </div>
                      {!formData.whatsappMismoNumero && (
                        <input
                          type="tel"
                          className="form-control form-control-sm mt-2"
                          placeholder="Numero de WhatsApp"
                          value={formData.whatsapp}
                          onChange={(e) => handleChange('whatsapp', e.target.value)}
                          disabled={loading}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Horario */}
              <div className="mb-3">
                <label className="form-label">
                  {tipo === 'establecimiento' ? 'Horario' : 'Horario de atencion'}
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={tipo === 'establecimiento' ? 'Ej: Lunes a Viernes 9am - 7pm' : 'Ej: Lunes a Sabado 8am - 6pm'}
                  value={formData.horario}
                  onChange={(e) => handleChange('horario', e.target.value)}
                  disabled={formData.is24h || loading}
                />
                {tipo === 'establecimiento' && (
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="is24h"
                      checked={formData.is24h}
                      onChange={(e) => handleChange('is24h', e.target.checked)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="is24h">
                      Abierto 24 horas
                    </label>
                  </div>
                )}
              </div>

              {/* Direccion (solo establecimiento) */}
              {tipo === 'establecimiento' && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Direccion</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.direccion}
                      onChange={(e) => handleChange('direccion', e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label">Ciudad</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.ciudad}
                        onChange={(e) => handleChange('ciudad', e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Estado</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.estado}
                        onChange={(e) => handleChange('estado', e.target.value)}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Zonas (solo proveedor) */}
              {tipo === 'proveedor' && (
                <div className="mb-3">
                  <label className="form-label">Zonas donde trabaja</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Centro, Animas, Jardines (separar con comas)"
                    value={formData.zonas}
                    onChange={(e) => handleChange('zonas', e.target.value)}
                    disabled={loading}
                  />
                  <small className="text-muted">Separa las zonas con comas</small>
                </div>
              )}

              {/* INFORMACION ADICIONAL */}
              <hr className="my-4" />
              <h6 className="text-muted mb-3">
                <i className="bi bi-info-circle me-2"></i>
                INFORMACION ADICIONAL
              </h6>

              {/* Metodos de pago */}
              <div className="mb-3">
                <label className="form-label">Metodos de pago</label>
                <div className="d-flex flex-wrap gap-3">
                  {metodosPagoOpciones.map(metodo => (
                    <div className="form-check" key={metodo}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`pago-${metodo}`}
                        checked={formData.metodosPago.includes(metodo)}
                        onChange={() => handleCheckboxArrayChange('metodosPago', metodo)}
                        disabled={loading}
                      />
                      <label className="form-check-label text-capitalize" htmlFor={`pago-${metodo}`}>
                        {metodo}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Estacionamiento (solo establecimiento) */}
              {tipo === 'establecimiento' && (
                <div className="mb-3">
                  <label className="form-label">Estacionamiento</label>

                  {/* Tipo de estacionamiento */}
                  <div className="mb-2">
                    <small className="text-muted d-block mb-1">Tipo:</small>
                    <div className="d-flex flex-wrap gap-3">
                      {[
                        { id: 'propio', label: 'Estacionamiento propio' },
                        { id: 'calle', label: 'En la calle' },
                        { id: 'plaza', label: 'Plaza cercana' },
                        { id: 'sin', label: 'Sin estacionamiento' }
                      ].map(opcion => (
                        <div className="form-check" key={opcion.id}>
                          <input
                            type="radio"
                            className="form-check-input"
                            id={`estacionamiento-tipo-${opcion.id}`}
                            name="estacionamiento-tipo"
                            checked={formData.estacionamiento.tipo === opcion.id}
                            onChange={() => handleChange('estacionamiento', {
                              ...formData.estacionamiento,
                              tipo: opcion.id,
                              lugares: opcion.id !== 'propio' ? null : formData.estacionamiento.lugares
                            })}
                            disabled={loading}
                          />
                          <label className="form-check-label" htmlFor={`estacionamiento-tipo-${opcion.id}`}>
                            {opcion.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cantidad de lugares (solo si es propio) */}
                  {formData.estacionamiento.tipo === 'propio' && (
                    <div className="mb-2 ms-4">
                      <label className="form-label small">Cantidad de lugares:</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        style={{ width: '100px' }}
                        min="1"
                        max="100"
                        value={formData.estacionamiento.lugares || ''}
                        onChange={(e) => handleChange('estacionamiento', {
                          ...formData.estacionamiento,
                          lugares: e.target.value ? parseInt(e.target.value) : null
                        })}
                        disabled={loading}
                        placeholder="Ej: 5"
                      />
                    </div>
                  )}

                  {/* Facilidad */}
                  {formData.estacionamiento.tipo && (
                    <div className="mt-2">
                      <small className="text-muted d-block mb-1">Facilidad:</small>
                      <div className="d-flex flex-wrap gap-3">
                        {[
                          { id: 'muy_facil', label: 'Muy facil' },
                          { id: 'facil', label: 'Facil' },
                          { id: 'medio', label: 'Medio' },
                          { id: 'dificil', label: 'Dificil' }
                        ].map(opcion => (
                          <div className="form-check" key={opcion.id}>
                            <input
                              type="radio"
                              className="form-check-input"
                              id={`estacionamiento-facilidad-${opcion.id}`}
                              name="estacionamiento-facilidad"
                              checked={formData.estacionamiento.facilidad === opcion.id}
                              onChange={() => handleChange('estacionamiento', {
                                ...formData.estacionamiento,
                                facilidad: opcion.id
                              })}
                              disabled={loading}
                            />
                            <label className="form-check-label" htmlFor={`estacionamiento-facilidad-${opcion.id}`}>
                              {opcion.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Servicios */}
              {serviciosDisponibles.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Servicios</label>
                  <div className="d-flex flex-wrap gap-2">
                    {serviciosDisponibles.map(servicio => (
                      <div className="form-check" key={servicio}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`servicio-${servicio}`}
                          checked={formData.servicios.includes(servicio)}
                          onChange={() => handleCheckboxArrayChange('servicios', servicio)}
                          disabled={loading}
                        />
                        <label className="form-check-label" htmlFor={`servicio-${servicio}`}>
                          {servicio}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fotos */}
              <div className="mb-3">
                <label className="form-label">Fotos (1-3)</label>
                <div className="d-flex gap-2">
                  {[0, 1, 2].map(index => (
                    <div
                      key={index}
                      className="border rounded d-flex align-items-center justify-content-center bg-light"
                      style={{ width: '100px', height: '100px', cursor: loading ? 'default' : 'pointer' }}
                      onClick={() => !loading && alert('Funcion de subir foto - proximamente')}
                    >
                      {formData.fotos[index] ? (
                        <img
                          src={formData.fotos[index]}
                          alt={`Foto ${index + 1}`}
                          className="w-100 h-100 object-fit-cover rounded"
                        />
                      ) : (
                        <span className="text-muted text-center">
                          <i className="bi bi-plus fs-4"></i>
                          <br />
                          <small>Foto</small>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notas internas */}
              <div className="mb-3">
                <label className="form-label">Notas internas</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.notasInternas}
                  onChange={(e) => handleChange('notasInternas', e.target.value)}
                  disabled={loading}
                  placeholder="Notas visibles solo para administradores"
                />
              </div>

              {/* Estado */}
              <hr className="my-4" />
              <div className="mb-4">
                <label className="form-label">Estado</label>
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="status-pendiente"
                      name="status"
                      checked={formData.status === 'pendiente'}
                      onChange={() => handleChange('status', 'pendiente')}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="status-pendiente">
                      <span className="badge bg-warning text-dark me-1">
                        <i className="bi bi-clock"></i>
                      </span>
                      Pendiente
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="status-verificado"
                      name="status"
                      checked={formData.status === 'verificado'}
                      onChange={() => handleChange('status', 'verificado')}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="status-verificado">
                      <span className="badge bg-success me-1">
                        <i className="bi bi-check"></i>
                      </span>
                      Verificado
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="status-rechazado"
                      name="status"
                      checked={formData.status === 'rechazado'}
                      onChange={() => handleChange('status', 'rechazado')}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="status-rechazado">
                      <span className="badge bg-danger me-1">
                        <i className="bi bi-x"></i>
                      </span>
                      Rechazado
                    </label>
                  </div>
                </div>
              </div>

              {/* Google Places Linking (solo en modo edicion y para establecimientos) */}
              {isEditing && tipo === 'establecimiento' && (
                <>
                  <hr className="my-4" />
                  <h6 className="text-muted mb-3">
                    <i className="bi bi-google me-2"></i>
                    VINCULACION CON GOOGLE
                  </h6>

                  <div className="card bg-light mb-4">
                    <div className="card-body py-3">
                      {currentNegocio?.googlePlaceId ? (
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="badge bg-success me-2">
                              <i className="bi bi-link-45deg me-1"></i>
                              Vinculado
                            </span>
                            {currentNegocio.rating && (
                              <span className="text-muted">
                                <i className="bi bi-star-fill text-warning me-1"></i>
                                {currentNegocio.rating}
                                <small className="ms-1">({currentNegocio.reviewCount || 0} resenas)</small>
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setShowGoogleModal(true)}
                            disabled={loading}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            Gestionar
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted">
                            <i className="bi bi-info-circle me-2"></i>
                            No vinculado con Google Places
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => setShowGoogleModal(true)}
                            disabled={loading}
                          >
                            <i className="bi bi-link-45deg me-1"></i>
                            Vincular
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Liga de edicion (solo en modo edicion) */}
              {isEditing && (
                <>
                  <hr className="my-4" />
                  <h6 className="text-muted mb-3">
                    <i className="bi bi-link-45deg me-2"></i>
                    LIGA DE EDICION
                  </h6>
                  <p className="small text-muted mb-3">
                    Genera una liga temporal para que el negocio pueda actualizar sus datos sin iniciar sesion.
                    La liga es valida por 24 horas.
                  </p>

                  <div className="card bg-light mb-4">
                    <div className="card-body py-3">
                      {editLink ? (
                        <div>
                          {/* Liga activa */}
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <span className="badge bg-success me-2">
                                <i className="bi bi-check-circle me-1"></i>
                                Liga activa
                              </span>
                              <small className="text-muted">
                                Expira: {new Date(editLink.expiresAt).toLocaleString('es-MX', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </small>
                            </div>
                          </div>

                          {/* Input con la liga */}
                          <div className="input-group mb-2">
                            <input
                              type="text"
                              className="form-control form-control-sm bg-white"
                              value={editLink.link}
                              readOnly
                            />
                            <button
                              type="button"
                              className={`btn btn-sm ${editLinkCopied ? 'btn-success' : 'btn-outline-primary'}`}
                              onClick={handleCopiarEditLink}
                              disabled={loadingEditLink}
                            >
                              {editLinkCopied ? (
                                <>
                                  <i className="bi bi-check me-1"></i>
                                  Copiado
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-clipboard me-1"></i>
                                  Copiar
                                </>
                              )}
                            </button>
                          </div>

                          {/* Acciones */}
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={handleInvalidarEditLink}
                              disabled={loadingEditLink}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Invalidar
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={handleGenerarEditLink}
                              disabled={loadingEditLink}
                            >
                              <i className="bi bi-arrow-repeat me-1"></i>
                              Regenerar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted">
                            <i className="bi bi-info-circle me-2"></i>
                            No hay liga activa
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleGenerarEditLink}
                            disabled={loadingEditLink}
                          >
                            {loadingEditLink ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Generando...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-link-45deg me-1"></i>
                                Generar liga
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Boton guardar */}
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Guardar
                  </>
                )}
              </button>
            </form>
          </>
        )}

        {/* Tab: Seguimiento (CRM) */}
        {activeTab === 'seguimiento' && isEditing && (
          <SeguimientoTab
            negocio={currentNegocio}
            onNegocioUpdated={handleNegocioUpdated}
            addToHistory={addToHistory}
          />
        )}
      </div>

      {/* Modal de vinculacion con Google */}
      {isEditing && (
        <VincularGoogleModal
          show={showGoogleModal}
          onClose={() => setShowGoogleModal(false)}
          negocio={currentNegocio}
          onNegocioUpdated={handleNegocioUpdated}
        />
      )}
    </div>
  );
}
